// Autocomplete for task tokens: @members, #labels, +collections, due:/t:/start: dates.
import {
  autocompletion,
  type Completion,
  type CompletionContext,
  type CompletionResult,
} from "@codemirror/autocomplete";
import { syntaxTree } from "@codemirror/language";
import type { Extension } from "@codemirror/state";
import { actorHandle } from "../lib/format";
import { editorContext } from "./context";
import { resolveDate } from "./tasktokens";

const DATE_OPTIONS: { label: string; detail: string }[] = [
  { label: "today", detail: "Today" },
  { label: "tomorrow", detail: "Tomorrow" },
  { label: "mon", detail: "Next Monday" },
  { label: "tue", detail: "Next Tuesday" },
  { label: "wed", detail: "Next Wednesday" },
  { label: "thu", detail: "Next Thursday" },
  { label: "fri", detail: "Next Friday" },
  { label: "sat", detail: "Next Saturday" },
  { label: "sun", detail: "Next Sunday" },
  { label: "+1w", detail: "In one week" },
  { label: "+2w", detail: "In two weeks" },
  { label: "+1m", detail: "In one month" },
];

function inCode(ctx: CompletionContext): boolean {
  for (let n: ReturnType<typeof syntaxTree>["topNode"] | null = syntaxTree(ctx.state).resolveInner(ctx.pos, -1); n; n = n.parent) {
    if (n.name === "FencedCode" || n.name === "CodeBlock" || n.name === "InlineCode") return true;
  }
  return false;
}

/** Make a collection name usable as a +project token (no spaces). */
export function projectToken(name: string): string {
  return name.trim().replace(/\s+/g, "-");
}

export function taskTokenSource(ctx: CompletionContext): CompletionResult | null {
  if (inCode(ctx)) return null;
  const data = ctx.state.facet(editorContext)();

  const date = ctx.matchBefore(/(?:^|\s)(?:due|t|start):[\w+-]*$/);
  if (date) {
    const colon = date.text.indexOf(":");
    const from = date.from + colon + 1;
    const now = data.now ? data.now() : new Date();
    const options: Completion[] = DATE_OPTIONS.map((o) => ({
      label: o.label,
      detail: `${o.detail} · ${resolveDate(o.label, now)?.date ?? ""}`,
      type: "constant",
      apply: `${o.label} `,
    }));
    return { from, options, validFor: /^[\w+-]*$/ };
  }

  const tok = ctx.matchBefore(/(?:^|\s)[@#+][\p{L}\p{N}_.\-:/]*$/u);
  if (!tok) return null;
  const lead = /^\s/.test(tok.text) ? 1 : 0;
  const from = tok.from + lead;
  const sigil = tok.text[lead];
  if (!ctx.explicit && tok.text.length - lead === 0) return null;

  let options: Completion[] = [];
  if (sigil === "@") {
    options = data.members.map((a) => ({
      label: `@${actorHandle(a)}`,
      detail: a.display_name,
      type: "variable",
      apply: `@${actorHandle(a)} `,
    }));
  } else if (sigil === "#") {
    // "#" at line start is a heading, not a label.
    const line = ctx.state.doc.lineAt(from);
    if (from === line.from) return null;
    options = data.labels.map((l) => ({ label: `#${l}`, type: "keyword", apply: `#${l} ` }));
  } else if (sigil === "+") {
    const line = ctx.state.doc.lineAt(from);
    if (/^\s*$/.test(line.text.slice(0, from - line.from))) return null; // "+" list marker
    options = data.collections.map((c) => ({
      label: `+${projectToken(c)}`,
      detail: c,
      type: "namespace",
      apply: `+${projectToken(c)} `,
    }));
  }
  if (options.length === 0) return null;
  return { from, options, validFor: /^[@#+][\p{L}\p{N}_.\-:/]*$/u };
}

export function taskAutocomplete(): Extension {
  return autocompletion({ override: [taskTokenSource], activateOnTyping: true, icons: false });
}
